import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { DropdownTrigger } from '@usepepper/ui/drafts/dropdown-trigger';
import { formatMonthDayInTimezone, formatTimeslot } from '@/utils/date';
import { GetAvailableOrderFulfillmentResponse } from '@/types';
import { getFulfillmentDisplayName } from '@/experiences/sales-portal/blocs/utils/fulfillment-utils';
import { SavedSelectedFulfillment } from '@/experiences/sales-portal/domain/ordering/blocs/stores/selected-fulfillment-store';
import { TimeZoneMap } from '@domain/fulfillment-settings/types/types';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

interface FulfillmentTypeAndDateTriggerProps {
  availableFulfillments?: GetAvailableOrderFulfillmentResponse[];
  disabled?: boolean;
  isOpen: boolean;
  onPress: () => void;
  savedSelectedFulfillment?: SavedSelectedFulfillment | null;
  timezones: TimeZoneMap;
}

export const FulfillmentTypeAndDateTrigger: React.FC<
  FulfillmentTypeAndDateTriggerProps
> = ({
  availableFulfillments,
  disabled,
  isOpen,
  onPress,
  savedSelectedFulfillment,
  timezones,
}) => {
  const [styles, theme] = useStyles();
  const textColor = disabled
    ? theme.sk.color.text.disabled
    : theme.sk.color.text.primary;
  // Derive date and type from savedSelectedFulfillment
  const date = savedSelectedFulfillment?.date ?? null;
  const fulfillmentType = savedSelectedFulfillment?.type ?? null;
  const formattedDate = date
    ? formatMonthDayInTimezone(date, timezones.restaurant)
    : '';
  const selectedFulfillmentDisplay = getFulfillmentDisplayName(
    fulfillmentType,
    availableFulfillments ?? [],
  );

  // Format timeslot if present
  const formattedTimeslot = savedSelectedFulfillment?.timeslot
    ? formatTimeslot(savedSelectedFulfillment.timeslot, timezones, true)
    : '';

  return (
    <DropdownTrigger
      disabled={disabled}
      isOpen={isOpen}
      onPress={onPress}
      style={styles.trigger}
    >
      <View style={styles.fulfillmentTypeAndDate}>
        <Typography color={textColor} variant="paragraph-md">
          {selectedFulfillmentDisplay}
        </Typography>
        <Typography color={textColor} variant="paragraph-md-600">
          {[formattedDate, formattedTimeslot].filter(Boolean).join(' ')}
        </Typography>
      </View>
    </DropdownTrigger>
  );
};

const useStyles = createStyles((theme) => ({
  fulfillmentTypeAndDate: {
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceXsmall,
  },
  trigger: {
    minWidth: 148,
  },
}));
